import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sparkles, Heart, Calendar, Truck, Crown, Apple } from "lucide-react";

const Auth = () => {
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next") || "/dashboard";
  const appleAuthError = new URLSearchParams(location.search).get("apple_error");
  // Reciprocity/Wallet loop — arriving here to claim a gift defaults to "Create account".
  const [mode, setMode] = useState(nextParam.startsWith("/experience/") ? "register" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // The Sovereign Key — "Sign in with Google" (Google Identity Services). Only renders once a
  // real GOOGLE_CLIENT_ID is configured server-side — never a fake/non-functional button.
  const [googleConfig, setGoogleConfig] = useState({ enabled: false, client_id: "" });
  const [googleError, setGoogleError] = useState("");
  const googleBtnRef = useRef(null);

  // Apple Identity Mandate (Guideline 4.8) — One-Tap Sovereign Auth. Only renders live once a
  // real Apple Services ID (APPLE_CLIENT_ID) is configured server-side — never a fake button.
  const [appleConfig, setAppleConfig] = useState({ enabled: false, client_id: "", redirect_uri: "" });
  const [appleError, setAppleError] = useState(
    appleAuthError ? "Sign in with Apple failed — please try again." : ""
  );

  useEffect(() => {
    api.get("/auth/google/config").then((r) => setGoogleConfig(r.data)).catch(() => {});
    api.get("/auth/apple/config").then((r) => setAppleConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!googleConfig.enabled || !googleConfig.client_id) return;
    const handleCredential = async (response) => {
      setGoogleError(""); setLoading(true);
      try {
        await loginWithGoogle(response.credential);
        navigate(nextParam);
      } catch (err) {
        setGoogleError(err?.response?.data?.detail || "Google Sign-In failed.");
      } finally { setLoading(false); }
    };
    const initAndRender = () => {
      if (!window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({ client_id: googleConfig.client_id, callback: handleCredential });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "filled_black", size: "large", shape: "pill", width: 360, text: "continue_with",
      });
    };
    if (window.google?.accounts?.id) { initAndRender(); return; }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = initAndRender;
    document.body.appendChild(script);
    return () => { script.onload = null; };
  }, [googleConfig, loginWithGoogle, navigate, nextParam]);

  // One-Tap Sovereign Auth — a full-page redirect straight to Apple's own /auth/authorize
  // page (NOT a JS-SDK popup). Apple's page performs the sign-in, then POSTs the result
  // (id_token included, response_mode=form_post) directly to our locked-down backend
  // callback (APPLE_REDIRECT_URI = https://www.mygiftedos.com/api/auth/apple/callback), which
  // verifies it, runs the Sovereign ID bridge, and redirects back with a ready session token.
  // No popup blockers, no client-side JS SDK fragility, no password-creation screen.
  const handleAppleSignIn = () => {
    if (!appleConfig.enabled || !appleConfig.client_id) {
      setAppleError("Sign in with Apple isn't connected yet — ask the Commander to finish the Apple Services ID setup.");
      return;
    }
    // Preserve any special destination (e.g. claiming a gift via /experience/:token); default
    // brand-new Apple sign-ins straight into the Maison Studio, not the generic dashboard.
    const appleNext = nextParam === "/dashboard" ? "/cards" : nextParam;
    const state = encodeURIComponent(JSON.stringify({ next: appleNext }));
    const authorizeUrl =
      `https://appleid.apple.com/auth/authorize?client_id=${encodeURIComponent(appleConfig.client_id)}` +
      `&redirect_uri=${encodeURIComponent(appleConfig.redirect_uri)}` +
      `&response_type=code%20id_token&response_mode=form_post&scope=name%20email&state=${state}`;
    window.location.href = authorizeUrl;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate(nextParam);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center glow-purple">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="gifted-brand text-3xl">GIFTED</span>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-serif text-5xl leading-tight mb-5">
            Never miss a moment that <span className="gold-text italic">matters</span>.
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            The Relationship OS that remembers, crafts, and mails the perfect card — so your
            people always feel seen.
          </p>
          <div className="space-y-3">
            {[
              { icon: Heart, t: "Profiles for every loved one" },
              { icon: Calendar, t: "14-day gifting countdowns" },
              { icon: Sparkles, t: "AI-crafted cards & art" },
              { icon: Truck, t: "Printed & mailed for you" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-secondary/20 border border-secondary/30 flex items-center justify-center">
                  <f.icon className="w-4 h-4 text-secondary" />
                </div>
                <span className="text-foreground/90">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-muted-foreground">The Future of Personalized Sentiment.</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md glass rounded-2xl p-8 animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-6 justify-center">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="gifted-brand text-2xl">GIFTED</span>
          </div>
          <h2 className="font-serif text-3xl mb-1">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p className="text-muted-foreground text-sm mb-6">
            {mode === "login" ? "Sign in to your Relationship OS" : "Start being unforgettably thoughtful"}
          </p>

          {/* The Sovereign Key — primary access method */}
          <div className="mb-3" data-testid="google-signin-section">
            {googleConfig.enabled ? (
              <div className="flex justify-center" ref={googleBtnRef} data-testid="google-signin-btn" />
            ) : (
              <button
                type="button"
                data-testid="google-signin-placeholder"
                onClick={() => setGoogleError("Sign in with Google isn't connected yet — ask the Commander to add a Google Client ID.")}
                className="w-full flex items-center justify-center gap-2.5 rounded-full py-3 border border-[#c9a24a]/40 text-sm font-medium hover:border-[#c9a24a]/70 transition-colors"
                style={{ background: "linear-gradient(135deg, rgba(246,227,163,0.12) 0%, rgba(201,162,74,0.10) 100%)" }}
              >
                <Crown className="w-4 h-4 gold-text" /> <span className="gold-text">Sign in with Google</span>
              </button>
            )}
            {googleError && <p className="text-xs text-red-400 mt-2 text-center">{googleError}</p>}
          </div>

          {/* Apple Identity Mandate (Guideline 4.8) — Auth Parity: same size/footprint as the
              Google button above, styled to Apple's HIG (black background, white glyph+label).
              One-Tap Sovereign Auth: a full-page redirect, no popup, no password screen. */}
          <div className="mb-5" data-testid="apple-signin-section">
            <button
              type="button"
              data-testid={appleConfig.enabled ? "apple-signin-btn" : "apple-signin-placeholder"}
              onClick={handleAppleSignIn}
              className="w-full flex items-center justify-center gap-2.5 rounded-full py-3 bg-black text-white text-sm font-medium hover:bg-black/85 transition-colors"
            >
              <Apple className="w-4 h-4 fill-white" />
              Sign in with Apple
            </button>
            {appleError && <p className="text-xs text-red-400 mt-2 text-center" data-testid="apple-signin-error">{appleError}</p>}
          </div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-sm text-muted-foreground">Full name</label>
                <input
                  data-testid="auth-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Jane Doe"
                />
              </div>
            )}
            <div>
              <label className="text-sm text-muted-foreground">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-muted-foreground">Password</label>
                {mode === "login" && (
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-primary hover:underline">Forgot password?</Link>
                )}
              </div>
              <input
                data-testid="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white font-medium rounded-lg py-3 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "New to GIFTED?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary hover:underline font-medium"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
